/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { BottomRight, TopLeft } from "@dojo/framework/core/meta/Dimensions";
import * as _ from "lodash";
import {
	ANGLE_ZERO,
	BLACK_COLOR,
	CIRCULAR_HANDLE_RADIUS,
	CURVE_CONTROL_POINT_STROKE,
	CURVE_EDIT_STROKE_COLOR,
	CURVE_POINT_DIFF,
	CURVE_SELECTION_STROKE_WIDTH,
	DEFAULT_CONTROL_POINT_RADIUS,
	HANDLE_COLOR,
	HANDLE_SNAPPING_WINDOW,
	MAX_Z_INDEX,
	MIN_DIMENSION,
	MIN_PEN_CURVE_RADIUS,
	MOVEMENT_THRESHOLD_PX,
	PERCENTAGE_HUNDRED,
	SELECTION_HANDLER_STROKE_WIDTH,
	SNAPPING_HANDLE_COLOR,
	STROKE_DASH_MAPPING,
	WHITE_COLOR,
	ZERO_TEXT,
} from "../../Constants";
import { mapGroupedPointToShapeMatrix, manipulateDimensions, xor } from "../../store/reducers/util";
import { IExtents } from "../interfaces/model/dataStructures";
import { ICustomShapePoint } from "../interfaces/model/dataStructures/CustomShapePoint";
import { CanvasMode, PathOperationTypes, Keys } from "../interfaces/model/enumerations/index";
import { IHandlePoint, IPenGeometry, HandleType } from "../interfaces/model/geometry/CustomGeometry";
import {
	IDrawCubicBezierCurveTo,
	IDrawLineTo,
	IShapePath,
	PathOperation,
	IMovePathTo,
	ICloseShapePath,
} from "../interfaces/model/geometry/ShapePath";
import { IShapePositionCoordinate } from "../interfaces/model/geometry/ShapePositionCoordinate";
import { IBrowserType } from "../interfaces/model/index";
import { IConnectionShape } from "../interfaces/model/PageObjects/ConnectionShape";
import { IShape, IGroupShape } from "../interfaces/model/PageObjects/index";
import { ILineProperties, IShapeProperties, ISolidFillProperties } from "../interfaces/model/properties/index";
import { cancelCurrentOperation } from "../keyUtil";
import CurveTransientPoint from "../meta/CurveTransientPoint";
import { IPosition } from "../meta/Drag/CsiDragController";
import { createPosition, CsiDrag, IDragResults } from "../meta/Drag/index";
import { getCustomGeometry } from "../selectors/geometry";
import { getZoomValue, getSnapPosition } from "../selectors/stateAwareConstants";
import * as util from "../util";
import { buildShapePointList, getMappedCurvePoint, getModifiedCurveDelta } from "../util";
import { CurveControlPoint } from "./CurveControlPoint";
import { DraggableWidget } from "./DraggableWidget";

interface IEditPenPointsProps {
	groupObject?: IGroupShape;

	/** The pageObject corresponding to image */
	pageObject: IShape | IConnectionShape;

	/** Size of the parent widget, it is required to compute offsets for edited shape. */
	parentSize: TopLeft & BottomRight;

	/**
	 * Selected curve point of curve
	 */
	selectedCurvePoint?: number;

	/**
	 * Browser type on which the app is running
	 */
	browserType: IBrowserType;

	/** delete curve point callback */
	deleteCurvePoint(): void;

	/** canvas container api */
	canvasContainerEl: any;

	/**
	 * Method to be invoked after path list is updated.
	 */
	setCurvePoints(pathLst: IShapePath[], x: number, y: number, width: number, height: number,
		transient?: boolean, offsetX?: number, offsetY?: number): void;

	setHandlePoints(pointLst?: IHandlePoint[]): void;

	/** Set selected curve point callback */
	setSelectedCurvePoint(curvePoint?: number, preserveSelection?: boolean): void;

	/** select object by id callback */
	selectObjectById(selectionId: string, isMultiSelect: boolean, trans: boolean, forceSingleSelect?: boolean): void;

	/** set canvas mode callback */
	setCanvasMode(mode: CanvasMode, preserveSelection?: boolean): void;

	//Whether shape is a crop shape
	isCropShape?: boolean;
}

/**
 * Edit curve points component, provides a way to adjust curve points.
 * After each drag setCurvePoints is invoked to update the shape.
 */
export class EditPenPoints extends DraggableWidget<IEditPenPointsProps>  {
	/* Used for adjusting scroll.  Keeps track of how far off the scroll is */
	private scrollOffset = { x: 0, y: 0 };
	/* Most recent canvas position, used to detect scroll changes and adjust scrollOffset */
	private oldCanvasPos?: IPosition;
	private static _SHAPE_TO_EDIT = "curveShape";
	private _boundHandlers: any[];
	private path: string;
	private geometry: IPenGeometry;
	private selectedPathOperationIndex?: number;
	/* Keeps track of a point being added, used in new handle calculations */
	private newPoint: IPosition;
	/* Used for the click position when a new point is added and snap-with-shift is being used */
	private proximityNewPoint?: IPosition;
	/* Point the preview line should be drawn to */
	private previewPoint?: IPosition;
	/* Preview of point to be added to the middle of the curve */
	private transientPoint?: IPosition;
	/* Fraction of distance through its curve the transient point is */
	private transientLength: number;
	/* Number of the curve the transient point will be added to */
	private transientIndex: number;
	/* If a point is being added in the middle of the shape, this is its position */
	private newShapePoint?: ICustomShapePoint;
	/* Mid-handle-drag, in case handles are to snap with shift, we keep track of the mouse position */
	private handleDragPosition?: IPosition;
	private isPointsUpdated = false;
	private isPointsUpdatedInLastRender = false;
	private snapping = false;
	private snappingHandle = "front";
	private newStraightHandlePosition?: IPosition;

	private handlePoint?: IPosition;
	private secondHandlePoint?: IPosition;

	private clickFirstPointStartPosition?: IPosition;

	/** Store clone of page, it will be used for all optimistic editing. */
	private pageObject?: IShape | IConnectionShape;

	/** Store custom shape points, all operations are performed on these points */
	private shapePoints: ICustomShapePoint[] = [];

	private handlePoints: IHandlePoint[] = [];

	/** Is dragging in progress, we will be changing stroke color when control point is being dragged. */
	private isDragging = false;

	private isDraggingNewPoint = false;

	private altKeyPressed = false;
	private ctrlKeyPressed = false;
	private shiftKeyPressed = false;

	constructor() {
		super();
		this._boundHandlers = [];
		/** Add event listeners for shape */
		[
			{ event: "pointerup", func: this._onShapePointerUp.bind(this) },
			{ event: "keydown", func: this._onKeyDown.bind(this) },
			{ event: "keyup", func: this._onKeyUp.bind(this) },
		].forEach((object) => {
			document.addEventListener(object.event, object.func);
			this._boundHandlers.push(object);
		});
	}

	/**
	 * Remove all pointer event listeners on shape
	 */
	protected onDetach(): void {
		this._boundHandlers.forEach((object) => document.removeEventListener(object.event, object.func));
		this.meta(CsiDrag)
			.remove("curveShapeForSelection");
		// set selected curve point to undefined on detached
		this.properties.setSelectedCurvePoint(undefined);
	}

	/**
	 * Run after every renders and return the decorated DNodes
	 * @param dNode The DNodes to run through the after renders
	 */
	protected runAfterRenders(dNode: DNode | DNode[]): DNode | DNode[] {
		// If below condition is true, means this is the second render after the curve point is deleted,
		// where the actual path dimensions are reflected in dom
		if (this.isPointsUpdatedInLastRender && !this.isPointsUpdated) {
			this.saveCurvePoints(true);
			this.isPointsUpdatedInLastRender = false;
		}
		// If last point is delete in edit curve, curve dimension changes so here making sure that render will
		// get called again and on next render saveCurvePoints(above snippet code)
		// will get the correct dimension to calculate the curve height and width.
		if (this.isPointsUpdated) {
			this.isPointsUpdatedInLastRender = true;
			this.isPointsUpdated = false;
			this.saveCurvePoints();
		}

		return dNode;
	}

	/**
	 * Handle pointer down event for shape. This is the start of dragging.  We set
	 * the new point to the clicked point and leave permanently adding the point to
	 * other functions.
	 * @param event {PointerEvent}
	 */
	protected _onPointerDown(event: PointerEvent) {
		const { pageObject } = this;
		// only if add point is allowed
		if (pageObject && !this.shapeIsClosed()) {
			const { groupObject, parentSize } = this.properties;
			// mapped the new point with curve
			this.newPoint = getSnapPosition(
				getMappedCurvePoint(
					pageObject, { x: event.pageX + this.scrollOffset.x, y: event.pageY + this.scrollOffset.y }, parentSize, groupObject,
				),
			);
			if (this.shiftKeyPressed) {
				this.proximityNewPoint = this.newPoint;
				this.newPoint = util.calculateAngleSnappedPoint(this.newPoint, this.shapePoints[this.shapePoints.length - 1].position as IPosition);
			}

			this.isDraggingNewPoint = true;
			this.isDragging = true;
		}
	}

	/**
	 * Handle pointer up event for shape.  If the point is close to the 
	 * start of the drag we add a line and if not we add a curve.
	 *
	 * @param event {PointerEvent}
	 */
	protected _onPointerUp(event: PointerEvent) {
		const { pageObject } = this;
		// only if add point is allowed
		if (pageObject && this.isDraggingNewPoint && !this.shapeIsClosed()) {
			// mapped the new point with curve
			let finalPoint = getSnapPosition(
				getMappedCurvePoint(pageObject, { x: event.pageX + this.scrollOffset.x, y: event.pageY + this.scrollOffset.y },
					this.properties.parentSize, this.properties.groupObject),
			);

			let isClose = util.getDistance(this.newPoint, finalPoint) < MIN_PEN_CURVE_RADIUS;
			if (this.shiftKeyPressed) {
				if (this.proximityNewPoint) {
					isClose = util.getDistance(this.proximityNewPoint, finalPoint) < MIN_PEN_CURVE_RADIUS;
				}
				finalPoint = util.calculateAngleSnappedPoint(finalPoint, this.newPoint);
			}

			if (isClose) {
				this.addPointToGeometry(this.newPoint);
				this.shapePoints = buildShapePointList(this.geometry);
				this.updateGeometry();
				this.saveCurvePoints();
			} else {
				this.addCurveToGeometry(finalPoint);
				this.shapePoints = buildShapePointList(this.geometry);
				this.updateGeometry();
				this.saveCurvePoints();
			}

			this.handlePoint = undefined;
			this.secondHandlePoint = undefined;
			this.isDragging = false;
			this.isDraggingNewPoint = false;
		}
	}

	/**
	 * Handle pointer move event for shape.  If we are dragging this
	 * modifies the handle, otherwise modifies the preview point.
	 *
	 * @param event {PointerEvent}
	 */
	protected _onPointerMove(event: PointerEvent) {
		const { pageObject } = this;
		const { canvasContainerEl, groupObject, parentSize } = this.properties;
		// only if add point is allowed
		if (pageObject) {
			const canvasPage = canvasContainerEl.querySelector(".csi-canvas-page-content") as HTMLElement;
			const container = canvasPage.getBoundingClientRect();
			if (!this.oldCanvasPos) {
				this.scrollOffset = { x: 0, y: 0 };
			} else {
				this.scrollOffset.x += (this.oldCanvasPos.x - container.left);
				this.scrollOffset.y += (this.oldCanvasPos.y - container.top);
			}
			this.oldCanvasPos = { x: container.left, y: container.top };

			const prePoint = { x: event.pageX + this.scrollOffset.x, y: event.pageY + this.scrollOffset.y };
			const handlePoint =
				getMappedCurvePoint(pageObject, { x: prePoint.x, y: prePoint.y }, parentSize, groupObject);

			const {
				isDragging,
				isDraggingNewPoint,
				newPoint,
				transientPoint,
			} = this;

			if (newPoint && isDraggingNewPoint) {
				// disable preview point, since the user is now clicking and dragging
				this.previewPoint = undefined;
				let handlePoint = getMappedCurvePoint(pageObject, { x: event.pageX + this.scrollOffset.x, y: event.pageY + this.scrollOffset.y },
					this.properties.parentSize, this.properties.groupObject);
				handlePoint = getSnapPosition(handlePoint);
				if (this.shiftKeyPressed) {
					handlePoint = util.calculateAngleSnappedPoint(handlePoint, this.newPoint);
				}
				this.handlePoint = handlePoint;

				const secondHandlePoint: IPosition = createPosition();
				secondHandlePoint.x = newPoint.x - (handlePoint.x - newPoint.x);
				secondHandlePoint.y = newPoint.y - (handlePoint.y - newPoint.y);

				// set the second handle point
				this.secondHandlePoint = secondHandlePoint;
			} else if (!isDragging && !transientPoint) {
				this.previewPoint = getSnapPosition(handlePoint);
			} else {
				this.previewPoint = undefined;
			}
			this.updatePathAndGeometry(pageObject.spPr);
			// mapped the new point with curve
			this.invalidate();
		}
	}

	/**
	 * Handle the case where the mouse was not moved and a line should be added
	 *
	 * @param event {PointerEvent}
	 */
	protected addPointToGeometry(point: IPosition, handlePoint?: IPosition) {
		const { pageObject } = this;
		// only if add point is allowed
		if (pageObject) {
			const pathOperations = pageObject.spPr.geometry.pathLst[0].operations;
			const { cx, cy } = pageObject.spPr.xfrm.ext;

			pathOperations.push({
				pt: point,
				type: PathOperationTypes.LINE_TO,
			} as IDrawLineTo);

			const newFrontHandle = undefined;
			const newBackHandle = point;

			const handlePoints = (pageObject.spPr.geometry as IPenGeometry).handlePoints;
			if (this.ctrlKeyPressed) {
				handlePoints[handlePoints.length - 1].frontHandle = undefined;
			}

			handlePoints.push({ frontHandle: newFrontHandle, backHandle: newBackHandle, type: HandleType.STRAIGHT });

			const geometryParams = getCustomGeometry({
				cy, cx, geometry: pageObject.spPr.geometry,
				shapeAvList: undefined,
			});
			// generate new path
			this.path = geometryParams.pathInfo.pathString;
		}
	}

	/**
	 * Handle the case where the mouse was moved and a curve should be added
	 *
	 * @param event {PointerEvent}
	 */
	protected addCurveToGeometry(handlePoint: IPosition) {
		const { newPoint, pageObject } = this;
		// only if add point is allowed
		if (pageObject) {
			const pathOperations = pageObject.spPr.geometry.pathLst[0].operations;
			const { cx, cy } = pageObject.spPr.xfrm.ext;

			const handlePoints = (pageObject.spPr.geometry as IPenGeometry).handlePoints;

			let oldHandlePoint = handlePoints[handlePoints.length - 1].frontHandle;
			if (!oldHandlePoint) {
				const uPoint = util.getPathEndPoint(pathOperations[pathOperations.length - 1]);
				oldHandlePoint = { x: uPoint.x as number, y: uPoint.y as number };
			}

			const secondHandlePoint: IPosition = { x: 0, y: 0 };
			secondHandlePoint.x = newPoint.x - (handlePoint.x - newPoint.x);
			secondHandlePoint.y = newPoint.y - (handlePoint.y - newPoint.y);

			const operation: IDrawCubicBezierCurveTo = {
				pts: [oldHandlePoint, secondHandlePoint, newPoint as IShapePositionCoordinate],
				type: PathOperationTypes.CUBIT_BEZ_TO,
			};

			pathOperations.push(operation);
			const newFrontHandle = handlePoint;
			const newBackHandle = secondHandlePoint;
			handlePoints.push({ frontHandle: newFrontHandle, backHandle: newBackHandle, type: HandleType.MIRRORED });

			const geometryParams = getCustomGeometry({
				cy, cx, geometry: pageObject.spPr.geometry,
				shapeAvList: undefined,
			});
			// generate new path
			this.path = geometryParams.pathInfo.pathString;
		}
	}

	protected closeShape() {
		const { pageObject } = this;
		// only if add point is allowed
		if (pageObject) {
			const pathOperations = pageObject.spPr.geometry.pathLst[0].operations;
			const { cx, cy } = pageObject.spPr.xfrm.ext;

			pathOperations.push({
				pt: _.cloneDeep(this.shapePoints[0].position),
				type: PathOperationTypes.LINE_TO,
			} as IDrawLineTo);
			pathOperations.push({
				type: PathOperationTypes.CLOSE,
			} as ICloseShapePath);

			if (this.ctrlKeyPressed) {
				const handlePoints = (pageObject.spPr.geometry as IPenGeometry).handlePoints;
				handlePoints[handlePoints.length - 1].frontHandle = undefined;
			}

			const geometryParams = getCustomGeometry({
				cy, cx, geometry: pageObject.spPr.geometry,
				shapeAvList: undefined,
			});
			// generate new path
			this.path = geometryParams.pathInfo.pathString;
		}
	}

	/**
	 * Handle pointer down event for shape. This adds a new point to the middle of the shape,
	 * and slices the relevant bezier curve into two new curves.
	 *
	 * @param event {PointerEvent}
	 */
	protected _onShapePointerDown(event: PointerEvent) {
		const { pageObject } = this;
		// only if add point is allowed
		if (pageObject) {
			const { groupObject, parentSize, setSelectedCurvePoint } = this.properties;
			// mapped the new point with curve
			this.newPoint = getMappedCurvePoint(pageObject, { x: event.pageX, y: event.pageY },
				parentSize, groupObject);
			const result = this.meta(CurveTransientPoint)
				.get(EditPenPoints._SHAPE_TO_EDIT, pageObject, parentSize, groupObject, this.shapePoints);
			this.transientLength = result.length;
			this.transientIndex = result.prevIndex;

			const { spPr } = pageObject as IShape;

			// find new index
			this.selectedPathOperationIndex = this.transientIndex + 1;
			// select added curve point
			setSelectedCurvePoint(this.selectedPathOperationIndex, true);
			if (!this.newShapePoint) {
				this.newShapePoint = {
					isCurvePoint: true,
					position: this.transientPoint || this.newPoint,
				};
				const handlePoints = (spPr.geometry as IPenGeometry).handlePoints;
				const index = this.selectedPathOperationIndex as number;
				this.shapePoints.splice(index, 0, this.newShapePoint);
				const firstPoint = this.shapePoints[index - 1].position as IPosition;
				const secondPoint = index == this.shapePoints.length - 1 ? this.shapePoints[0].position as IPosition : this.shapePoints[index + 1].position as IPosition;
				const nextIndex = index == handlePoints.length ? 0 : index;
				const prevIndex = index == 0 ? handlePoints.length - 1 : index - 1;
				const prevHandle = handlePoints[prevIndex];
				const nextHandle = handlePoints[nextIndex];
				const firstControlPoint = (prevHandle && prevHandle.frontHandle && prevHandle.type !== HandleType.STRAIGHT) ? prevHandle.frontHandle : _.cloneDeep(firstPoint);
				const secondControlPoint = (nextHandle && nextHandle.backHandle && nextHandle.type !== HandleType.STRAIGHT) ? nextHandle.backHandle : _.cloneDeep(secondPoint);

				const firstCurve = util.sliceBezier(firstPoint, firstControlPoint, secondControlPoint, secondPoint, this.transientLength);
				const secondCurve = util.sliceBezier(secondPoint, secondControlPoint, firstControlPoint, firstPoint, 1 - this.transientLength);

				const dest = this.newShapePoint.position as IPosition;
				const han = firstCurve.secondHandle;

				const firstDistance = util.getDistance(han, dest);
				const secondDistance = util.getDistance(secondCurve.secondHandle, dest);

				const frontToBack = secondDistance / firstDistance;

				const newX = dest.x + (dest.x - han.x) * frontToBack;
				const newY = dest.y + (dest.y - han.y) * frontToBack;

				const newFrontHandle = { x: newX, y: newY } as IPosition;

				const newBackHandle = firstCurve.secondHandle;
				const newHandle = { frontHandle: newFrontHandle, frontToBackRatio: frontToBack, backHandle: newBackHandle, type: HandleType.ASYMMETRIC };

				if ((nextHandle.type === HandleType.STRAIGHT || !nextHandle.backHandle) &&
					(prevHandle.type === HandleType.STRAIGHT || !prevHandle.frontHandle)) {
					this.newStraightHandlePosition = _.cloneDeep(this.newShapePoint.position as IPosition);
					newHandle.type = HandleType.STRAIGHT;
				} else {
					prevHandle.frontHandle = firstCurve.firstHandle;
					nextHandle.backHandle = secondCurve.firstHandle;
					this.newStraightHandlePosition = undefined;
				}

				handlePoints.splice(index, 0, newHandle);
				this.isDragging = true;
			}
			// update shape path and geometry
			this.updatePathAndGeometry(spPr);
		}
	}

	/**
	 * Pointer move event added on ghost shape to show the transient point
	 * @param event {PointerEvent}
	 */
	protected _onShapePointerMove(event: PointerEvent) {
		const { parentSize, groupObject } = this.properties;
		const { pageObject } = this;
		if (pageObject && !this.isDragging) {
			this.previewPoint = undefined;
			const result = this.meta(CurveTransientPoint)
				.get(EditPenPoints._SHAPE_TO_EDIT, pageObject, parentSize, groupObject, this.shapePoints);
			if (result) {
				this.transientPoint = {
					x: result.best.x,
					y: result.best.y,
				} as IPosition;
				this.transientLength = result.length;
				this.transientIndex = result.prevIndex;
				// If transient point is close to curve point with diff CURVE_POINT_DIFF 3 then do not show
				// transient point
				_.some(this.shapePoints, (point: ICustomShapePoint) => {
					if (point.position && this.transientPoint) {
						const pointX = point.position.x as number;
						const pointY = point.position.y as number;
						const transientPoint = this.transientPoint as IPosition;
						if (Math.abs(Math.round(pointX - transientPoint.x)) <= CURVE_POINT_DIFF
							&& Math.abs(Math.round(pointY - transientPoint.y)) <= CURVE_POINT_DIFF) {
							this.transientPoint = undefined;
						}
					}
					return !this.transientPoint;
				});
				this.invalidate();
			}
		}
	}

	protected _onHandleMove(event: PointerEvent) {
		this.previewPoint = undefined;
		this.invalidate();
	}

	/**
	 * Pointer out event added on ghost shape to remove transient point
	 */
	protected _onShapePointerOut() {
		this.transientPoint = undefined;
	}

	/**
	 * Update geometry and path
	 * @param spPr {IShapeProperties}
	 */
	protected updatePathAndGeometry(spPr: IShapeProperties) {
		this.updateGeometry();
		const { cx, cy } = spPr.xfrm.ext;

		const geometryParams = getCustomGeometry({
			cy, cx, geometry: this.geometry,
			shapeAvList: undefined,
		});
		// generate new path
		this.path = geometryParams.pathInfo.pathString;
	}

	/**
	 * Handle pointer up for the shape. This will be end of drag.
	 * We need to preserve the new point added due to drag event. Till now it was optimistic rendering,
	 * here were will call pageAPI to preserve the changes to model.
	 */
	protected _onShapePointerUp(event: PointerEvent) {
		if (this.clickFirstPointStartPosition) {
			const clickTarget = getMappedCurvePoint(this.properties.pageObject, { x: event.pageX, y: event.pageY },
				this.properties.parentSize, this.properties.groupObject);

			if (util.getDistance(this.clickFirstPointStartPosition, clickTarget) < MOVEMENT_THRESHOLD_PX) {
				this.closeShape();
				this.updateGeometry();
				this.saveCurvePoints();
			}
		}
		this.clickFirstPointStartPosition = undefined;
		if (this.handlePoint) {
			this._onPointerUp(event);
		} else {
			this.addControlPointCompleted();
		}
	}

	/**
	 * Save updated curve points to PageObject model.
	 * @param transient {boolean} transient flag
	 */
	protected saveCurvePoints(transient?: boolean) {
		const { pageObject } = this;
		if (pageObject) {
			const { shapePathLst, deltaOffX, deltaOffY, widthDiffPer, heightDiffPer, shapePathDelta, handlePoints } =
				getModifiedCurveDelta(pageObject as IShape | IConnectionShape);
			// call action creator to update page model.
			this.properties.setCurvePoints(shapePathLst, deltaOffX, deltaOffY,
				widthDiffPer, heightDiffPer, transient, shapePathDelta.x, shapePathDelta.y);
			this.properties.setHandlePoints(handlePoints);

			// reset all flags for the dragging.
			this.selectedPathOperationIndex = this.newShapePoint = this.pageObject = undefined;
			this.isDragging = false;
		}
	}

	/**
	 * Handle drag for the new point on the shape. We are rendering shape in optimistic way,
	 * shape model is not yet updated.
	 * As we move a new point will be added if it was not added before and then it will be dragged with pointer.
	 * As we drag a point, we need to update it's start & end control points
	 * and also start control point for next operation.
	 *
	 * @param dragResults {IDragResult} Meta corresponding to dragging.
	 * @param isComplete {boolean} drag complete flag.
	 */
	protected onDrag(dragResults: IDragResults, isComplete: boolean) {
		const { pageObject } = this;
		if (pageObject && !isComplete) {
			const mappedDelta = mapGroupedPointToShapeMatrix(this.properties.groupObject, pageObject, dragResults.delta);
			if (this.selectedPathOperationIndex && this.newShapePoint) {
				this.isDragging = true;
				const controlPointPosition = this.newShapePoint.position as IShapePositionCoordinate;
				controlPointPosition.x = controlPointPosition.x as number + mappedDelta.x;
				controlPointPosition.y = controlPointPosition.y as number + mappedDelta.y;
				this.handlePoints[this.selectedPathOperationIndex].frontHandle.x += mappedDelta.x;
				this.handlePoints[this.selectedPathOperationIndex].frontHandle.y += mappedDelta.y;
				this.handlePoints[this.selectedPathOperationIndex].backHandle.x += mappedDelta.x;
				this.handlePoints[this.selectedPathOperationIndex].backHandle.y += mappedDelta.y;
				// update shape path and geometry
				if (this.newStraightHandlePosition && util.getDistance(this.newStraightHandlePosition, controlPointPosition as IPosition) > MOVEMENT_THRESHOLD_PX) {
					this.handlePoints[this.selectedPathOperationIndex].type = HandleType.ASYMMETRIC;
				}
				this.updatePathAndGeometry(pageObject.spPr);
			}
		} else {
			// deselect the point if escape key is pressed to end drag
			if (cancelCurrentOperation(dragResults.keyData)) {
				this.properties.setSelectedCurvePoint(undefined);
			}
			this.addControlPointCompleted();
		}
	}

	/**
	 * Render shape for dimensions purpose. This shape is not visible, however, is exact copy of actual shape without
	 * rotation or flip applied. Hence, we get accurate dimensions for this shape while in edit. These dimensions are
	 * used for further computations for deciding changes in offsets and extents after edit operations.
	 * @param ext Shape extents
	 */
	protected renderShapeForDimensions(ext: IExtents): DNode | null {
		if (!this.pageObject) {
			return null;
		}
		const height = util.getMaxDimension(ext.cy);
		const width = util.getMaxDimension(ext.cx);

		const path = this.generateTempGeometry().path;

		return (
			<div
				styles={
					{
						height: `${height}px`,
						left: "0px",
						overflow: "visible",
						position: "absolute",
						top: "0px",
						transformOrigin: "center",
						width: `${width}px`,
					}}
			>
				<svg
					height={`${PERCENTAGE_HUNDRED}%`}
					preserveAspectRatio="none"
					styles={{
						overflow: "visible",
						top: ZERO_TEXT,
						left: ZERO_TEXT,
						position: "absolute",
					}}
					viewBox={`0 0 ${width} ${height}`}
					width={`${PERCENTAGE_HUNDRED}%`}
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d={`${path}`}
						fill="none"
						key="curveShapeForDimensions"
						pointer-events="none"
						stroke="none"
						stroke-width={`${CURVE_SELECTION_STROKE_WIDTH}`}
						vector-effect="non-scaling-stroke"
					/>
				</svg>
			</div>
		);
	}

	/**
	 * Key down event on document
	 * @param event {KeyboardEvent}
	 */
	protected generateHandleGeometry(snappingDisplay?: boolean) {
		const clonedPageObject = _.cloneDeep(this.pageObject);
		const pathOperations = clonedPageObject.spPr.geometry.pathLst[0].operations;
		const useOperations = [];
		const selectedPoint = util.getPathEndPoint(pathOperations[this.properties.selectedCurvePoint]);
		if ((this.pageObject.spPr.geometry as IPenGeometry).handlePoints[this.properties.selectedCurvePoint].type === HandleType.STRAIGHT) {
			return;
		}
		const handle = _.cloneDeep((this.pageObject.spPr.geometry as IPenGeometry).handlePoints[this.properties.selectedCurvePoint]);
		let handlePoint = handle.backHandle;
		let secondHandlePoint = handle.frontHandle;

		if (!handlePoint) {
			handlePoint = { x: selectedPoint.x as number, y: selectedPoint.y as number };
		}
		if (!secondHandlePoint) {
			secondHandlePoint = { x: selectedPoint.x as number, y: selectedPoint.y as number };
		}

		const cp = this.shapePoints[this.properties.selectedCurvePoint].position as IPosition;
		const fh = handle.frontHandle;
		const bh = handle.backHandle;
		if (snappingDisplay && this.snappingHandle === "front") {
			handle.frontToBackRatio = util.getDistance(fh, cp) / util.getDistance(bh, cp);
			handle.type = HandleType.DISCONNECTED;

			const ratio = handle.frontToBackRatio;
			const pointPosition = this.shapePoints[this.properties.selectedCurvePoint].position as IPosition;
			const secondHandlePoint = handle.frontHandle;
			secondHandlePoint.x = pointPosition.x - (bh.x - pointPosition.x) * ratio;
			secondHandlePoint.y = pointPosition.y - (bh.y - pointPosition.y) * ratio;
		} else if (snappingDisplay && this.snappingHandle === "back") {
			handle.frontToBackRatio = util.getDistance(fh, cp) / util.getDistance(bh, cp);
			handle.type = HandleType.DISCONNECTED;

			const ratio = handle.frontToBackRatio;
			const pointPosition = this.shapePoints[this.properties.selectedCurvePoint].position as IPosition;
			const secondHandlePoint = handle.backHandle;
			secondHandlePoint.x = pointPosition.x - (fh.x - pointPosition.x) / ratio;
			secondHandlePoint.y = pointPosition.y - (fh.y - pointPosition.y) / ratio;
		}

		const { cx, cy } = this.pageObject.spPr.xfrm.ext;

		const moveOperation: IMovePathTo = {
			pt: selectedPoint,
			type: PathOperationTypes.MOVE_TO,
		};
		const lineOperation: IDrawLineTo = {
			pt: handlePoint,
			type: PathOperationTypes.LINE_TO,
		};
		const secondMoveOperation: IMovePathTo = {
			pt: selectedPoint,
			type: PathOperationTypes.MOVE_TO,
		};
		const lineOperation2: IDrawLineTo = {
			pt: secondHandlePoint,
			type: PathOperationTypes.LINE_TO,
		};

		useOperations.push(moveOperation);
		useOperations.push(lineOperation);
		useOperations.push(secondMoveOperation);
		useOperations.push(lineOperation2);

		clonedPageObject.spPr.geometry.pathLst = [{ operations: useOperations }];

		const geometryParams = getCustomGeometry({
			cy, cx, geometry: clonedPageObject.spPr.geometry,
			shapeAvList: undefined,
		});
		// generate new path
		return { path: geometryParams.pathInfo.pathString, operations: pathOperations, handle };
	}

	/**
	 * Key down event on document
	 * @param event {KeyboardEvent}
	 */
	protected generatePreviewGeometry() {
		if (!this.previewPoint) {
			return;
		}
		let usePoint = this.previewPoint;

		const clonedPageObject = _.cloneDeep(this.pageObject);
		const pathOperations = clonedPageObject.spPr.geometry.pathLst[0].operations;
		const useOperations = [];
		const selectedPoint = util.getPathEndPoint(pathOperations[pathOperations.length - 1]) as IPosition;
		if (this.shiftKeyPressed) {
			usePoint = util.calculateAngleSnappedPoint(usePoint, selectedPoint);
		}

		const { cx, cy } = this.pageObject.spPr.xfrm.ext;

		const moveOperation: IMovePathTo = {
			pt: selectedPoint,
			type: PathOperationTypes.MOVE_TO,
		};
		useOperations.push(moveOperation);
		if (this.ctrlKeyPressed) {
			const lineOperation: IDrawLineTo = {
				pt: usePoint,
				type: PathOperationTypes.LINE_TO,
			};
			useOperations.push(lineOperation);
		} else {
			const firstHandle = util.getHandleValue(this.handlePoints[this.handlePoints.length - 1], true, this.shapePoints[this.shapePoints.length - 1].position as IPosition);
			const bezOperation: IDrawCubicBezierCurveTo = {
				pts: [firstHandle, usePoint, usePoint],
				type: PathOperationTypes.CUBIT_BEZ_TO,
			};
			useOperations.push(bezOperation);
		}

		clonedPageObject.spPr.geometry.pathLst = [{ operations: useOperations }];

		const geometryParams = getCustomGeometry({
			cy, cx, geometry: clonedPageObject.spPr.geometry,
			shapeAvList: undefined,
		});
		// generate new path
		return { path: geometryParams.pathInfo.pathString, operations: pathOperations };
	}

	/**
	 * Key down event on document
	 * @param event {KeyboardEvent}
	 */
	protected generateTempGeometry() {
		const { pageObject } = this;
		if (this.handlePoint && this.isDragging) {
			const clonedPageObject = _.cloneDeep(pageObject);
			const pathOperations = clonedPageObject.spPr.geometry.pathLst[0].operations;
			const { cx, cy } = pageObject.spPr.xfrm.ext;

			const handlePoints = (clonedPageObject.spPr.geometry as IPenGeometry).handlePoints;
			let oldHandlePoint = handlePoints[handlePoints.length - 1].frontHandle;

			if (!oldHandlePoint) {
				const pathEndPoint = util.getPathEndPoint(pathOperations[pathOperations.length - 1]);
				oldHandlePoint = { x: pathEndPoint.x as number, y: pathEndPoint.y as number };
			}

			const operation: IDrawCubicBezierCurveTo = {
				pts: [oldHandlePoint, this.secondHandlePoint, this.newPoint as IShapePositionCoordinate],
				type: PathOperationTypes.CUBIT_BEZ_TO,
			};

			const lineOperation: IDrawLineTo = {
				pt: this.handlePoint,
				type: PathOperationTypes.LINE_TO,
			};
			const secondMoveOperation: IMovePathTo = {
				pt: this.newPoint,
				type: PathOperationTypes.MOVE_TO,
			};
			const lineOperation2: IDrawLineTo = {
				pt: this.secondHandlePoint,
				type: PathOperationTypes.LINE_TO,
			};

			pathOperations.push(operation);
			pathOperations.push(lineOperation);
			pathOperations.push(secondMoveOperation);
			pathOperations.push(lineOperation2);

			const geometryParams = getCustomGeometry({
				cy, cx, geometry: clonedPageObject.spPr.geometry,
				shapeAvList: undefined,
			});
			// generate new path
			return { path: geometryParams.pathInfo.pathString, operations: pathOperations };
		} else {
			return { path: this.path, operations: pageObject.spPr.geometry.pathLst[0].operations };
		}
	}

	/**
	 * Key down event on document
	 * @param event {KeyboardEvent}
	 */
	protected _onKeyDown(event: KeyboardEvent) {
		const isAltKey = event.keyCode === 18;
		const isEscapeKey = event.keyCode === Keys.ESCAPE;
		const isEnterKey = event.keyCode === Keys.ENTER;
		const isCMDKey = event.keyCode === 224 || event.keyCode === 17 || event.keyCode === 91 || event.keyCode === 93;
		const isCtrlKey = event.keyCode === 17;
		const isShiftKey = event.keyCode === Keys.SHIFT;
		if (isEnterKey || isEscapeKey) {
			this.properties.setCanvasMode(CanvasMode.SELECT, true);
		}
		const isMac = navigator.userAgent.indexOf("Mac OS") !== -1;
		if (!isMac && isCtrlKey) {
			this.ctrlKeyPressed = true;
			this.invalidate();
		}
		if (isMac && isCMDKey) {
			this.ctrlKeyPressed = true;
			this.invalidate();
		}
		if (isAltKey) {
			this.altKeyPressed = true;
		}
		if (isShiftKey) {
			this.shiftKeyPressed = true;
		}
	}

	protected _onKeyUp(event: KeyboardEvent) {
		const isCtrlKey = event.keyCode === 17;
		const isAltKey = event.keyCode === 18;
		const isMac = navigator.userAgent.indexOf("Mac OS") !== -1;
		const isCMDKey = event.keyCode === 224 || event.keyCode === 17 || event.keyCode === 91 || event.keyCode === 93;
		const isShiftKey = event.keyCode === Keys.SHIFT;
		if (!isMac && isCtrlKey) {
			this.ctrlKeyPressed = false;
		}
		if (isMac && isCMDKey) {
			this.ctrlKeyPressed = false;
		}
		if (isAltKey) {
			this.altKeyPressed = false;
		}
		if (isShiftKey) {
			this.shiftKeyPressed = false;
		}
	}

	protected render() {
		// clone page object if it's not cloned already. Clone is reset when operation is complete.
		const { groupObject } = this.properties;
		if (!this.pageObject || (!this.isDragging)) {
			// clone page object
			this.pageObject = _.cloneDeep(this.properties.pageObject);

			// build geometry
			const { spPr } = this.pageObject as IShape;
			this.geometry = spPr.geometry as IPenGeometry;

			util.updateShapePathList(this.geometry.pathLst, 0, 0);

			this.shapePoints = buildShapePointList(this.geometry);
			this.handlePoints = this.geometry.handlePoints;
			// Delete last point if it's CLOSE point, for close point position is undefined
			const lastPoint = this.shapePoints[this.shapePoints.length - 1];
			if (lastPoint && !lastPoint.position) {
				this.shapePoints.pop();
			}
		}
		let scale: string = "";
		const spPr = this.pageObject.spPr;
		let { flipH, rot } = spPr.xfrm;
		const { off, ext } = spPr.xfrm;

		let offX = off.x;
		let offY = off.y;

		if (groupObject !== undefined) {
			const resultObj = manipulateDimensions.calculateSubgroupAbsoluteOffsets(spPr, groupObject.spPr);

			offX = resultObj.offX;
			offY = resultObj.offY;
			rot = resultObj.rot;

			flipH = xor(flipH, groupObject.spPr.xfrm.flipH);
		}

		const geometryParams = getCustomGeometry({
			cx: ext.cx, cy: ext.cy, geometry: this.geometry,
			shapeAvList: undefined,
		});
		this.path = geometryParams.pathInfo.pathString;
		if (flipH) {
			scale = "scale(-1, 1)";
		}
		rot = rot || ANGLE_ZERO;
		const transformOrigin = "center";
		const [width, height] = [getZoomValue(util.getMaxDimension(ext.cx)), getZoomValue(util.getMaxDimension(ext.cy))];

		const canvasPage = this.properties.canvasContainerEl.querySelector(".csi-canvas-page-content") as HTMLElement;
		const container = canvasPage.getBoundingClientRect();

		return (
			<div
				key={`pageObject_${this.properties.key}`}
				styles={
					{
						display: "inline-block",
						left: `${offX}px`,
						overflow: "visible",
						position: "absolute",
						top: `${offY}px`,
					}}
			>
				{this.renderShapeForDimensions(ext)}
				<div
					styles={
						{
							height: `${height}px`,
							overflow: "visible",
							position: "absolute",
							transform: `rotate(${rot}deg) ${scale}`,
							transformOrigin: `${transformOrigin}`,
							width: `${width}px`,
							zIndex: `${MAX_Z_INDEX}`,
						}}
				>
					<div
						onpointerdown={this._onPointerDown}
						onpointermove={this._onPointerMove}
						onpointerup={this._onPointerUp}
						styles={
							{
								pointerEvents: "all",
								position: "absolute",
								cursor: "crosshair",
								left: `-${offX}px`,
								top: `-${offY}px`,
								height: `${getZoomValue(container.height)}px`,
								width: `${getZoomValue(container.width)}px`,
								zIndex: `${MAX_Z_INDEX - 1}`,
							}}
					/>
					<svg
						height={`${PERCENTAGE_HUNDRED}%`}
						preserveAspectRatio="none"
						styles={{
							cursor: "crosshair",
							left: ZERO_TEXT,
							overflow: "visible",
							position: "absolute",
							top: ZERO_TEXT,
							zIndex: `${MAX_Z_INDEX}`,
						}}
						viewBox={`0 0 ${width} ${height}`}
						width={`${PERCENTAGE_HUNDRED}%`}
						xmlns="http://www.w3.org/2000/svg"
					>
						{this.renderShape()}
						{this.renderHandles()}
						{this.renderPreview()}
						<g onpointermove={this._onHandleMove}>
							{this.renderControlPoints()}
						</g>
					</svg>
				</div>
			</div>
		);
	}

	private renderHandles(): DNode[] {
		const { pageObject } = this;
		// Return empty array if there is no pageObject
		if (!pageObject || this.properties.selectedCurvePoint === undefined) {
			return [];
		}
		this.requestDragMeta("curveShapeForSelection");

		const geometry = pageObject.spPr.geometry as IPenGeometry;

		const snappingDisplay = (
			this.snapping &&
			this.isDragging &&
			this.handlePoints[this.properties.selectedCurvePoint].type === HandleType.DISCONNECTED
		);
		const stroke = snappingDisplay ? SNAPPING_HANDLE_COLOR : HANDLE_COLOR;

		// If dragging is in progress, we want to highlight path with different color.
		const opacity = this.isDragging ? 1 : 0.5;
		const strokeWidth = 2;
		// path stroke dasharray
		const dash = "none";

		const handleGeometry = this.generateHandleGeometry(snappingDisplay);
		if (!handleGeometry) {
			return [];
		}
		const path = handleGeometry.path;

		const shapes = [];
		const selectedHandle = handleGeometry.handle;
		const firstHandle = selectedHandle?.backHandle;
		const secondHandle = selectedHandle?.frontHandle;
		const size = DEFAULT_CONTROL_POINT_RADIUS * 2;

		if (firstHandle && this.isDragging) {
			shapes.push(
				<rect
					fill={WHITE_COLOR}
					height={`${size}`}
					key="ghost_pen_first_handle"
					pointer-events="none"
					stroke={HANDLE_COLOR}
					stroke-width={`${getZoomValue(SELECTION_HANDLER_STROKE_WIDTH)}`
					}
					width={`${size}`}
					x={`${firstHandle.x - size / 2}`}
					y={`${firstHandle.y - size / 2}`}
				/>,
			);
		}
		if (secondHandle && this.isDragging) {
			shapes.push(
				<rect
					fill={WHITE_COLOR}
					height={`${size}`}
					key="ghost_pen_second_handle"
					pointer-events="none"
					stroke={HANDLE_COLOR}
					stroke-width={`${getZoomValue(SELECTION_HANDLER_STROKE_WIDTH)}`}
					width={`${size}`}
					x={`${secondHandle.x - size / 2}`}
					y={`${secondHandle.y - size / 2}`}
				/>,
			);
		}

		shapes.push(
			<path
				d={`${path}`}
				fill="none"
				key="ghost_pen_line"
				onpointermove={this._onHandleMove}
				pointer-events="stroke"
				stroke={`${stroke}`}
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth}`}
				styles={{ opacity: `${opacity}` }}
				vector-effect="non-scaling-stroke"
			/>,
		);
		return shapes;
	}

	private shapeIsClosed(): boolean {
		const operations = this.pageObject.spPr.geometry.pathLst[0].operations;
		return operations[operations.length - 1].type === PathOperationTypes.CLOSE;
	}

	private renderPreview(): DNode[] {// Return empty array if there is no pageObject
		if (!this.pageObject || this.shapeIsClosed()) {
			return [];
		}
		// Drag meta
		this.requestDragMeta("curveShapeForSelection");

		const stroke = HANDLE_COLOR;

		// If dragging is in progress, we want to highlight path with different color.
		const opacity = this.isDragging ? 1 : 0.5;
		const strokeWidth = this.pageObject.spPr.ln.W;
		// path stroke dasharray
		const dash = "none";

		const handleGeometry = this.generatePreviewGeometry();
		if (!handleGeometry) {
			return [];
		}
		const path = handleGeometry.path;

		const shape = (
			<path
				d={`${path}`}
				fill="none"
				stroke={`${stroke}`}
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth}`}
				styles={{ opacity: `${opacity}` }}
				vector-effect="non-scaling-stroke"
			/>
		);
		return [shape];
	}

	/**
	 * Render the custom shape. This shape is required to handle any new control point addition.
	 *
	 * Here, we render the same shapes with different aspect for different needs.
	 * Consider following layer diagram for further explanation.
	 *
	 * Z index -------------->
	 * |  Page layer                        | Edit layer                                        |
	 * |                                    |                                                   |
	 * ---------------------------------------------------------------------------------------------
	 * |                                    |                                                   |
	 * |  [Curve]     [some other page      | [ Black  | [ White  |  [ optimistic    | [ ghost  |
	 * |               object which is      | stroke   | stroke   |    shape ]       | shape ]  |
	 * |               on top of curve ]    | shape ]  | shape ]  |                  |          |
	 * |     A                  X           |    B     |    W     |      D           |    G     |
	 * |                                    |          |          |                  |          |
	 *
	 * Purpose of each layer ---
	 * A -> Clear purpose - to show curve in the page
	 *
	 * D -> Curve to show when user drags a control point forming change in the actual curve. While dragging, the A
	 * curve is not changed this gives the user the initial state of curve, whereas D gives the probable new state. This
	 * we call as optimistic rendered shape where real shape is not altered until user completes drag by releasing the
	 * control used to change curve.
	 * While dragging, it is drawn with lightSkyblue to show the optimistic rendering.
	 * While not dragging, it is shown with same line colour that of A, however with some transparency. This
	 * transparency creates an illusion as following
	 *   1. The pixels where D and A are intersecting, it results in A only (because of same pixel colour, even though D
	 *      being transparent)
	 *   2. The pixels where D and X are intersecting, it results in X colour with D's transparent factor.
	 * The overall effect is, where back sit curve is visible prior to the curve edit mode is seen as it is, and the
	 * remaining portion (which was hidden cause of back sit) is shown as light transparent above X
	 *
	 * G -> A ghost shape. Not shown to user. This shape provides a "right of way" to add new control points on curve.
	 * Thus, this curve is somewhat bigger (currently, at 18px) in width than actual shape.
	 *
	 * W -> There would be some situations where X and A are of same colour. Yet, to show D in such situation we have
	 * added a contributing layer by using W. This lightens the effect showing D (now W + D) in such situations.
	 *
	 * B -> At times, X and (W+D) will also result in same colour. This is handled with B in same way like W, however,
	 * B is with a unit less that W in thickness. Thus completing the overall issue while showing D (now B + W + D )
	 */
	private renderShape(): DNode[] {
		// Return empty array if there is no pageObject
		if (!this.pageObject) {
			return [];
		}
		// Drag meta
		this.requestDragMeta("curveShapeForSelection");

		const lineProps = this.pageObject.spPr.ln as ILineProperties;
		const lineFill = lineProps && lineProps.fill as ISolidFillProperties;

		let stroke;
		if (this.properties.isCropShape) {
			stroke = BLACK_COLOR;
		} else if (this.isDragging) {
			stroke = CURVE_EDIT_STROKE_COLOR;
		} else {
			stroke = lineFill && lineFill.color || "black";
		}

		// If dragging is in progress, we want to highlight path with different color.
		const opacity = this.isDragging ? 1 : 0.1;
		const strokeWidth = lineProps && lineProps.W || 2;
		// path stroke dasharray
		const dash = lineProps && lineProps.dash || "none";

		const path = this.generateTempGeometry().path;

		const shape = (
			<path
				d={`${path}`}
				fill="none"
				key={`${EditPenPoints._SHAPE_TO_EDIT}`}
				stroke={`${stroke}`}
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth}`}
				styles={{ opacity: `${opacity}` }}
				vector-effect="non-scaling-stroke"
			/>
		);
		// Black and white shape stroke added with opacity 0.1 so that it will look as transparent line
		// over all the shapes that overlapping the curve
		const blackStrokeShape = (
			<path
				d={`${path}`}
				fill="none"
				key={`${EditPenPoints._SHAPE_TO_EDIT}`}
				stroke="black"
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth - 1}`}
				styles={{ opacity: "0.1" }}
				vector-effect="non-scaling-stroke"
			/>
		);
		const whiteStrokeShape = (
			<path
				d={`${path}`}
				fill="none"
				key={`${EditPenPoints._SHAPE_TO_EDIT}`}
				stroke="white"
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth}`}
				styles={{ opacity: "0.1" }}
				vector-effect="non-scaling-stroke"
			/>
		);
		const ghostShape = (
			<path
				d={`${path}`}
				fill="none"
				key="curveShapeForSelection"
				onpointerdown={this._onShapePointerDown}
				onpointermove={this._onShapePointerMove}
				onpointerout={this._onShapePointerOut}
				pointer-events="stroke"
				stroke="none"
				stroke-width={`${CURVE_SELECTION_STROKE_WIDTH}`}
				styles={{ cursor: "crosshair" }}
				vector-effect="non-scaling-stroke"
			/>
		);
		return [blackStrokeShape, whiteStrokeShape, shape, ghostShape];
	}

	/**
	 * Render control points for the curve.
	 */
	private renderControlPoints(): DNode[] {
		const { selectedCurvePoint, isCropShape } = this.properties;
		// here we are assuming that we are going to have only single path list.
		let { operations } = this.properties.pageObject.spPr.geometry.pathLst[0];
		if (this.geometry) {
			operations = this.geometry.pathLst[0].operations;
		}
		const useOperations = operations[operations.length - 1].type == PathOperationTypes.CLOSE ? operations.slice(0, operations.length - 2) : operations;
		const handlePoints = this.handlePoints;

		const controlPoints = useOperations.map((operation: PathOperation, index: number) => {
			if (operation.type !== PathOperationTypes.CLOSE) {
				return (
					<CurveControlPoint
						isCropShape={isCropShape}
						isSelectedPoint={selectedCurvePoint === index}
						key={index}
						onPointClick={this.onControlPointClick}
						onPointDblClick={this.onControlPointDoubleClick}
						onPointDrag={this.translateControlPoint}
						onPointDragComplete={this.translateControlPointDone}
						onPointerDown={this.onControlPointPointerDown}
						operation={operation}
					/>
				);
			}
		});
		if (selectedCurvePoint !== undefined && handlePoints[selectedCurvePoint] && !(handlePoints[selectedCurvePoint].type === HandleType.STRAIGHT)) {
			let operation = ({
				pt: handlePoints[selectedCurvePoint].backHandle,
				type: PathOperationTypes.LINE_TO,
			} as IDrawLineTo);
			controlPoints.push(
				<CurveControlPoint
					hide={this.isDragging}
					isCropShape={isCropShape}
					isHandle
					isSelectedPoint={false}
					key="first_handle_point"
					onPointDrag={this.translateHandlePoint}
					onPointDragComplete={this.translateHandlePointDone}
					operation={operation}
				/>,
			);
			operation = ({
				pt: handlePoints[selectedCurvePoint].frontHandle,
				type: PathOperationTypes.LINE_TO,
			} as IDrawLineTo);
			controlPoints.push(
				<CurveControlPoint
					hide={this.isDragging}
					isCropShape={isCropShape}
					isHandle
					isSelectedPoint={false}
					key="second_handle_point"
					onPointDrag={this.translateFrontHandlePoint}
					onPointDragComplete={this.translateFrontHandlePointDone}
					operation={operation}
				/>,
			);
		}
		if (this.handlePoint && this.isDragging) {
			const lineOperation: IDrawLineTo = {
				pt: this.handlePoint,
				type: PathOperationTypes.LINE_TO,
			};
			const lineOperation2: IDrawLineTo = {
				pt: this.secondHandlePoint,
				type: PathOperationTypes.LINE_TO,
			};
			controlPoints.push(
				<CurveControlPoint
					isCropShape={isCropShape}
					isHandle
					isSelectedPoint={false}
					key="first_dragging_handle_point"
					onPointDrag={this.translateFrontHandlePoint}
					onPointDragComplete={this.translateFrontHandlePointDone}
					operation={lineOperation}
				/>,
			);
			controlPoints.push(
				<CurveControlPoint
					isCropShape={isCropShape}
					isHandle
					isSelectedPoint={false}
					key="second_dragging_handle_point"
					onPointDrag={this.translateFrontHandlePoint}
					onPointDragComplete={this.translateFrontHandlePointDone}
					operation={lineOperation2}
				/>,
			);
		}
		if (this.transientPoint) {
			controlPoints.push(
				<circle
					cx={`${this.transientPoint.x}`}
					cy={`${this.transientPoint.y}`}
					fill={`${CURVE_CONTROL_POINT_STROKE}`}
					key="transient-point"
					r={`${CIRCULAR_HANDLE_RADIUS}`}
					stroke={`${BLACK_COLOR}`}
					stroke-width={`${MIN_DIMENSION}`}
				/>);
		}
		return controlPoints;
	}

	/**
	 * Passed as parameter to adjustable point. This method is invoked on pointer down on the point.
	 * @param data {any} data to identify clicked point.
	 */
	private onControlPointClick(data: any) {
		// set selected curve point
		this.properties.setSelectedCurvePoint(data.operationIndex);
	}

	private onControlPointPointerDown(data: any) {
		if (!this.shapeIsClosed() && this.shapePoints.length > 2 && data.operationIndex == 0) {
			this.clickFirstPointStartPosition = this.shapePoints[0].position as IPosition;
		}
	}

	private onControlPointDoubleClick(data: any) {
		// set selected curve point
		const { spPr } = this.properties.pageObject;
		const index = data.operationIndex;
		const selectedIndex = this.properties.selectedCurvePoint;
		if (index !== selectedIndex) {
			this.properties.setSelectedCurvePoint(data.operationIndex);
		}
		if ((index === this.handlePoints.length - 1 || index === 0) && !this.shapeIsClosed()) {
			return;
		}
		let handle = this.handlePoints && this.handlePoints[index];
		const points = this.shapePoints;
		const pointPosition = points[index].position as IPosition;
		if (handle.type === HandleType.STRAIGHT && index !== 0 && index !== this.handlePoints.length - 1) {
			handle = util.generateNewHandles(points[index - 1].position as IPosition, pointPosition, points[index + 1].position as IPosition);
		} else if (handle.type === HandleType.STRAIGHT && index === 0 && this.shapeIsClosed()) {
			handle = util.generateNewHandles(points[points.length - 1].position as IPosition, pointPosition, points[index + 1].position as IPosition);
		} else if (handle.type === HandleType.STRAIGHT && index === points.length - 1 && this.shapeIsClosed()) {
			handle = util.generateNewHandles(points[index - 1].position as IPosition, pointPosition, points[0].position as IPosition);
		} else if (handle.type === HandleType.MIRRORED) {
			handle.type = HandleType.STRAIGHT;
		} else {
			const frontDist = handle.frontHandle ? util.getDistance(handle.frontHandle, pointPosition) : -1;
			const backDist = handle.backHandle ? util.getDistance(handle.backHandle, pointPosition) : -1;

			let handlePointPosition = handle.backHandle;
			let secondHandlePoint = handle.frontHandle;

			if (frontDist > backDist) {
				handlePointPosition = handle.frontHandle;
				secondHandlePoint = handle.backHandle;
			}
			secondHandlePoint.x = pointPosition.x - (handlePointPosition.x - pointPosition.x);
			secondHandlePoint.y = pointPosition.y - (handlePointPosition.y - pointPosition.y);
			handle.type = HandleType.MIRRORED;
		}
		(spPr.geometry as IPenGeometry).handlePoints[index] = handle;
		this.handlePoints[index] = handle;
		this.updateGeometry();
		this.invalidate();
		this.saveCurvePoints();
	}

	/**
	 * Passes as parameter to CurveControlPoint. This method is invoked when a control point is dragged.
	 * @param data {any} data to identify clicked point.
	 * @param dx {number} x change in point.
	 * @param dy {number} y change in point.
	 */
	private translateControlPoint(data: any, dx: number, dy: number) {
		// set selected curve point
		if (!this.isDragging) {
			this.properties.setSelectedCurvePoint(data.operationIndex, true);
		}
		this.isDragging = true;
		const index = data.operationIndex;
		const mappedDelta = mapGroupedPointToShapeMatrix(this.properties.groupObject, this.properties.pageObject, { x: dx, y: dy });
		const controlPointPosition = this.shapePoints && this.shapePoints[index] && this.shapePoints[index].position;
		if (this.handlePoints[index]) {
			const handlePoint = this.handlePoints[index];
			if (handlePoint.frontHandle) {
				handlePoint.frontHandle.x += mappedDelta.x;
				handlePoint.frontHandle.y += mappedDelta.y;
			}
			if (handlePoint.backHandle) {
				handlePoint.backHandle.x += mappedDelta.x;
				handlePoint.backHandle.y += mappedDelta.y;
			}
		}
		if (controlPointPosition) {
			controlPointPosition.x = controlPointPosition.x as number + mappedDelta.x;
			controlPointPosition.y = controlPointPosition.y as number + mappedDelta.y;
		}
		this.updateGeometry(index === this.shapePoints.length);
	};

	private translateHandlePointHelper(dx: number, dy: number, movedHandle, otherHandle) {
		// set selected curve point
		this.isDragging = true;
		const index = this.properties.selectedCurvePoint;
		const mappedDelta = mapGroupedPointToShapeMatrix(this.properties.groupObject, this.properties.pageObject, { x: dx, y: dy });
		const handle = this.handlePoints && this.handlePoints[index];
		if (this.altKeyPressed) {
			handle.type = HandleType.DISCONNECTED;
		}
		if (!this.handleDragPosition) {
			this.handleDragPosition = _.cloneDeep(movedHandle);
		}
		let handlePointPosition = this.handleDragPosition;
		handlePointPosition.x = (handlePointPosition.x as number) + mappedDelta.x;
		handlePointPosition.y = (handlePointPosition.y as number) + mappedDelta.y;
		if (this.shiftKeyPressed) {
			handlePointPosition = util.calculateAngleSnappedPoint(handlePointPosition, this.shapePoints[index].position as IPosition);
		}
		movedHandle.x = handlePointPosition.x;
		movedHandle.y = handlePointPosition.y;
		if (otherHandle) {
			if (handlePointPosition && handle.type === HandleType.ASYMMETRIC) {
				const pointPosition = this.shapePoints[index].position as IPosition;
				const ratio = util.getDistance(otherHandle, pointPosition) / util.getDistance(movedHandle, pointPosition);
				const secondHandlePoint = otherHandle;
				secondHandlePoint.x = pointPosition.x - (handlePointPosition.x - pointPosition.x) * ratio;
				secondHandlePoint.y = pointPosition.y - (handlePointPosition.y - pointPosition.y) * ratio;

				this.snapping = false;
			} else if (handlePointPosition && handle.type === HandleType.MIRRORED) {
				const pointPosition = this.shapePoints[index].position as IPosition;
				const secondHandlePoint = otherHandle;
				secondHandlePoint.x = pointPosition.x - (handlePointPosition.x - pointPosition.x);
				secondHandlePoint.y = pointPosition.y - (handlePointPosition.y - pointPosition.y);

				this.snapping = false;
			} else if (handle.type === HandleType.DISCONNECTED && handle.backHandle && handle.frontHandle) {
				const cp = this.shapePoints[index].position as IPosition;
				const fh = handle.frontHandle;
				const bh = handle.backHandle;

				const firstSide = (fh.y - cp.y) / (fh.x - cp.x);
				const secondSide = (cp.y - bh.y) / (cp.x - bh.x);

				const firstAngle = Math.atan(firstSide);
				const secondAngle = Math.atan(secondSide);

				if (Math.abs(firstAngle - secondAngle) < HANDLE_SNAPPING_WINDOW) {
					this.snapping = true;
					this.snappingHandle = "back";
				} else {
					this.snapping = false;
				}
			}
		}
		this.updateGeometry();
		this.invalidate();
	}

	private translateHandlePoint(data: any, dx: number, dy: number) {
		const index = this.properties.selectedCurvePoint;
		const handle = this.handlePoints && this.handlePoints[index];
		this.translateHandlePointHelper(dx, dy, handle.backHandle, handle.frontHandle);
	};

	private translateFrontHandlePoint(data: any, dx: number, dy: number) {
		const index = this.properties.selectedCurvePoint;
		const handle = this.handlePoints && this.handlePoints[index];
		this.translateHandlePointHelper(dx, dy, handle.frontHandle, handle.backHandle);
	};

	/**
	 * Update shape geometry using modified shapePoints.
	 * @param {boolean} isLastPoint
	 */
	private updateGeometry(isLastPoint?: boolean) {
		// clone geometry before we modify it
		this.scrollOffset = { x: 0, y: 0 };
		this.oldCanvasPos = undefined;
		this.geometry = _.cloneDeep(this.geometry);
		const shapePathLst = this.geometry.pathLst;
		const shapePath = shapePathLst[0];

		// generate path operation list using shapePoints
		if (this.shapePoints) {
			const newPathOperationList = util.generatePenOperationList(this.shapePoints, this.handlePoints, this.shapeIsClosed());

			shapePath.operations = newPathOperationList;
		}
		// update geometry with new path operation list.
		if (this.pageObject) {
			this.geometry.handlePoints = this.handlePoints;
			this.pageObject.spPr.geometry = this.geometry;
		}
	}

	/**
	 * Save curve points once dragging is complete.
	 */
	private translateControlPointDone(data: any, dragResult: IDragResults) {
		// deselect the point if escape key is pressed to end drag
		if (cancelCurrentOperation(dragResult.keyData)) {
			this.properties.setSelectedCurvePoint(undefined);
		}
		this.saveCurvePoints();
	}

	/**
	 * Helper function for finishing handle point drag
	 */
	private translateHandlePointDoneHelper(dragResult: IDragResults, isBack: boolean) {
		// deselect the point if escape key is pressed to end drag
		const selectedIndex = this.properties.selectedCurvePoint;
		const selectedHandlePoint = this.handlePoints[selectedIndex];
		this.handleDragPosition = undefined;
		if (cancelCurrentOperation(dragResult.keyData)) {
			this.properties.setSelectedCurvePoint(undefined);
		} else if (selectedHandlePoint.type === HandleType.DISCONNECTED && selectedHandlePoint.frontHandle && selectedHandlePoint.backHandle) {
			const cp = this.shapePoints[selectedIndex].position as IPosition;
			const fh = selectedHandlePoint.frontHandle;
			const bh = selectedHandlePoint.backHandle;

			const firstSide = (fh.y - cp.y) / (fh.x - cp.x);
			const secondSide = (cp.y - bh.y) / (cp.x - bh.x);

			const firstAngle = Math.atan(firstSide);
			const secondAngle = Math.atan(secondSide);

			if (Math.abs(firstAngle - secondAngle) < HANDLE_SNAPPING_WINDOW) {
				selectedHandlePoint.frontToBackRatio = util.getDistance(fh, cp) / util.getDistance(bh, cp);
				selectedHandlePoint.type = HandleType.ASYMMETRIC;

				const ratio = selectedHandlePoint.frontToBackRatio;
				const pointPosition = this.shapePoints[selectedIndex].position as IPosition;
				if (isBack) {
					const secondHandlePoint = selectedHandlePoint.backHandle;
					secondHandlePoint.x = pointPosition.x - (fh.x - pointPosition.x) / ratio;
					secondHandlePoint.y = pointPosition.y - (fh.y - pointPosition.y) / ratio;
				} else {
					const secondHandlePoint = selectedHandlePoint.frontHandle;
					secondHandlePoint.x = pointPosition.x - (bh.x - pointPosition.x) * ratio;
					secondHandlePoint.y = pointPosition.y - (bh.y - pointPosition.y) * ratio;
				}
			}
		}
		this.saveCurvePoints();
	}

	/**
	 * Save curve points once dragging is complete.
	 */
	private translateHandlePointDone(data: any, dragResult: IDragResults) {
		this.translateHandlePointDoneHelper(dragResult, true);
	}

	/**
	 * Save curve points once dragging is complete.
	 */
	private translateFrontHandlePointDone(data: any, dragResult: IDragResults) {
		this.translateHandlePointDoneHelper(dragResult, false);
	}

	/**
	 * Add control point to curve on drag complete
	 */
	private addControlPointCompleted() {
		if (this.selectedPathOperationIndex) {
			this.saveCurvePoints();
		}
	}
};

export default EditPenPoints;
